/**
 * What needs a person today, and what this deployment will and will not do.
 *
 * Everything here is read from records the server holds. Nothing is estimated:
 * a count of zero is shown as zero, and a deployment whose switches could not
 * be read says so rather than assuming they are on.
 */
import type {
  ReportDeliveryHistoryItem,
  ReportSchedule,
} from '@/pages/pipeline-intelligence/types'
import type { ReportSchedulerStatus } from '@/services/reportSchedules'
import { compareInstants, oneLine, parseInstant } from './format'
import { scheduleHealth, type ScheduleHealth } from './scheduleHealth'

/** The window the "failed" count covers. The design asks for the last 7 days. */
export const FAILED_WINDOW_DAYS = 7

export type DeploymentTone = 'ok' | 'warn' | 'bad'

export type DeploymentNotice = {
  tone: DeploymentTone
  eyebrow: string
  title: string
  body: string
}

/**
 * The one sentence about whether scheduled reports run and whether anything is
 * emailed. `null` while the switches are unread, so the page never claims a
 * state it has not seen.
 */
export function deploymentNotice(status: ReportSchedulerStatus | undefined): DeploymentNotice | null {
  if (!status) return null
  if (!status.generationEnabled) {
    return {
      tone: 'warn',
      eyebrow: 'DEPLOYMENT',
      title: 'Scheduled reports are switched off on this deployment',
      body: 'Schedules are saved, but none runs on its own and nothing is emailed. Run now still produces any report on demand.',
    }
  }
  if (!status.deliveryEnabled) {
    return {
      tone: 'warn',
      eyebrow: 'DELIVERY',
      title: 'Email delivery is off on this deployment',
      body: 'Reports still generate and are kept. Nothing is emailed until reviewer or team delivery is switched on and recipients are approved.',
    }
  }
  if (status.allowlistConfigured === false) {
    return {
      tone: 'warn',
      eyebrow: 'DELIVERY',
      title: 'No recipient is approved for email yet',
      body: 'Email delivery is on, but the approved-recipient list is empty, so nothing is sent. Reports still generate and are kept.',
    }
  }
  return {
    tone: 'ok',
    eyebrow: 'DELIVERY',
    title: 'Scheduled email is on',
    body: 'Reports generate on time and are emailed to approved recipients.',
  }
}

export type DeploymentSwitch = { label: string; value: string; on: boolean }

/** The six switches the detail page lays out. Booleans only; never an address or a setting value. */
export function deploymentSwitches(status: ReportSchedulerStatus | undefined): DeploymentSwitch[] {
  if (!status) return []
  const onOff = (label: string, on: boolean): DeploymentSwitch => ({ label, on, value: on ? 'On' : 'Off' })
  const allowlist = status.allowlistConfigured
  return [
    onOff('Report generation', status.generationEnabled),
    onOff('Email delivery', status.deliveryEnabled),
    onOff('Reviewer delivery', status.reviewerDeliveryEnabled),
    onOff('Team delivery', status.teamDeliveryEnabled),
    allowlist === undefined
      ? { label: 'Approved recipients', on: false, value: 'Unknown' }
      : { label: 'Approved recipients', on: allowlist, value: allowlist ? 'Set' : 'None set' },
    onOff('SLA digest', status.slaDigestEnabled),
  ]
}

export type FailureEntry = {
  id: string
  at: string | null
  scheduleId: string
  scheduleName: string
  kind: 'run' | 'delivery'
  recipient: string | null
  reason: string
}

export type AttentionSummary = {
  deployment: DeploymentNotice | null
  failures: {
    total: number
    runs: number
    deliveries: number
    items: FailureEntry[]
    /**
     * False when the ledger read hit its limit inside the window: runs older than the oldest one read may
     * hold more failures, so the count is a floor and "none failed" cannot be said.
     */
    complete: boolean
  }
  schedules: {
    overdue: ScheduleHealth[]
    noNextRun: ScheduleHealth[]
    drafts: ScheduleHealth[]
    /** Distinct schedules that need someone, by id. */
    needingAction: string[]
  }
  /** The soonest run that will really happen, or why none will. */
  nextRun:
    | { kind: 'due'; at: string; text: string; scheduleName: string; timeZone: string }
    | { kind: 'none'; reason: string }
}

export function summarizeAttention(inputs: {
  schedules: ReportSchedule[]
  deliveries: ReportDeliveryHistoryItem[]
  status?: ReportSchedulerStatus
  now?: Date
  /** How many runs the ledger read was asked for; with it, a read that filled up is known to be partial. */
  deliveryRunLimit?: number
}): AttentionSummary {
  const now = inputs.now ?? new Date()
  const nameOf = new Map(inputs.schedules.map((schedule) => [schedule.id, schedule.name]))
  const windowStart = now.valueOf() - FAILED_WINDOW_DAYS * 86_400_000

  // The ledger returns the latest runs up to its limit. If it returned that many and the oldest of them is
  // still inside the window, the window may reach back past what was read.
  const runIds = new Set(inputs.deliveries.map((row) => row.runId))
  const oldestRead = inputs.deliveries
    .map((row) => parseInstant(row.scheduledFor ?? row.sentAt ?? row.completedAt ?? null))
    .filter((when): when is Date => Boolean(when))
    .reduce<number | null>((oldest, when) => (oldest === null || when.valueOf() < oldest ? when.valueOf() : oldest), null)
  const complete = !(
    inputs.deliveryRunLimit !== undefined &&
    runIds.size >= inputs.deliveryRunLimit &&
    oldestRead !== null &&
    oldestRead >= windowStart
  )

  const items: FailureEntry[] = []
  for (const row of inputs.deliveries) {
    const status = String(row.status ?? '').toLowerCase()
    if (status !== 'failed' && status !== 'bounced') continue
    const at = row.completedAt ?? row.sentAt ?? row.scheduledFor ?? null
    const when = parseInstant(at)
    if (!when || when.valueOf() < windowStart) continue
    const recipient = row.recipient ?? null
    items.push({
      id: row.attemptId ?? row.runId,
      at,
      scheduleId: row.scheduleId,
      scheduleName: nameOf.get(row.scheduleId) ?? row.scheduleName,
      kind: recipient ? 'delivery' : 'run',
      recipient,
      reason: oneLine(row.error) || (recipient ? 'The mail provider gave no reason' : 'No reason was recorded'),
    })
  }
  items.sort((a, b) => compareInstants(b.at, a.at))

  const health = inputs.schedules.map((schedule) => ({
    schedule,
    health: scheduleHealth(schedule, { now, status: inputs.status }),
  }))
  const overdue = health.filter((entry) => entry.health.attention === 'overdue').map((entry) => entry.health)
  const noNextRun = health.filter((entry) => entry.health.attention === 'no_next_run').map((entry) => entry.health)
  const drafts = health.filter((entry) => entry.health.attention === 'draft').map((entry) => entry.health)
  const needingAction = health
    .filter((entry) => ['overdue', 'no_next_run', 'draft'].includes(entry.health.attention ?? ''))
    .map((entry) => entry.schedule.id)

  const upcoming = health
    .filter((entry) => entry.health.nextRunAt)
    .sort((a, b) => compareInstants(a.health.nextRunAt, b.health.nextRunAt))[0]

  let nextRun: AttentionSummary['nextRun']
  if (upcoming) {
    nextRun = {
      kind: 'due',
      at: upcoming.health.nextRunAt as string,
      text: upcoming.health.nextRunText,
      scheduleName: upcoming.schedule.name,
      timeZone: upcoming.schedule.timezone,
    }
  } else if (inputs.status && !inputs.status.generationEnabled) {
    nextRun = { kind: 'none', reason: 'Nothing is scheduled to run: scheduled generation is off on this deployment.' }
  } else if (!inputs.schedules.some((schedule) => schedule.isActive)) {
    nextRun = { kind: 'none', reason: 'No schedule is active.' }
  } else {
    nextRun = { kind: 'none', reason: 'No active schedule has a run coming up.' }
  }

  return {
    deployment: deploymentNotice(inputs.status),
    failures: {
      total: items.length,
      runs: items.filter((item) => item.kind === 'run').length,
      deliveries: items.filter((item) => item.kind === 'delivery').length,
      items,
      complete,
    },
    schedules: { overdue, noNextRun, drafts, needingAction: [...new Set(needingAction)] },
    nextRun,
  }
}
